// Update your existing useGoogleAuthReturn hook
import { useEffect, useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthenticationFunctions } from "../../../components/authentication/utils/AuthenticationUtility";
import { IUserModel } from "./useAuthentication";
import { getErrorToast, getSuccessToast } from "../utilities/toastUtility";
import axiosInstance, { setAxiosToken } from "../helpers/axiosHelper";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import { isGoogleMeetConnectReturn } from "../utilities/googleMeetConnectUtility";
import { getSafeReturnUrl } from "../utilities/returnUrlUtility";

export const useGoogleAuthReturn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { onSuccessfulUserLogin } = useAuthenticationFunctions();
  const processedRef = useRef(false);
  const processedCodesRef = useRef(new Set<string>()); // Track processed OAuth codes
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    const cleanup = setInterval(() => {
      if (processedCodesRef.current.size > 10) {
        processedCodesRef.current.clear();
      }
    }, 60000);

    return () => clearInterval(cleanup);
  }, []);

  // Keep your existing fetchUserProfile function unchanged
  const fetchUserProfile = useCallback(async (token: string) => {
    try {
      setIsLoadingProfile(true);

      // Security: Validate JWT token expiry before using
      try {
        const tokenParts = token.split(".");
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const expiryTime = new Date(payload.exp * 1000);
          const currentTime = new Date();

          if (expiryTime <= currentTime) {
            throw new Error("JWT token has expired");
          }
        }
      } catch (decodeError) {
        throw new Error("Invalid JWT token format");
      }

      // Set the token for the API request
      setAxiosToken(token);

      // Make API request to get user profile
      const response = await axiosInstance.get(
        endpointsMetadata.authentication.getUserProfile
      );

      // Create user object from API response
      const userProfile: IUserModel = {
        id: response.data.id,
        authToken: token,
        firstName: response.data.firstName || "",
        lastName: response.data.lastName || "",
        phoneNumber: response.data.phoneNumber || "",
        role: response.data.role || "User",
        isUnreliable: response.data.isUnreliable || false,
        permissions: response.data.permissions || [],
      };

      return userProfile;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        getErrorToast("Невалиден токен за автентикация");
      } else if (error?.response?.status === 404) {
        getErrorToast("Потребителят не е намерен");
      } else {
        getErrorToast("Грешка при зареждане на потребителския профил");
      }

      return null;
    } finally {
      setIsLoadingProfile(false);
    }
  }, []);

  const handleGoogleAuthReturn = useCallback(async () => {

    // The code on a Google Meet connect return (Моят профил → Интеграции) is for the Meet card, not
    // a Google login. Sending it to the login endpoint would consume it before the card can.
    if (isGoogleMeetConnectReturn(location.search)) {
      return;
    }

    // Only process if we haven't already processed AND there are relevant OAuth parameters
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");
    const error = urlParams.get("error");
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const oauthReturn = urlParams.get("oauth_return");
    
    // Skip if no OAuth-related parameters are present
    if (!token && !error && !code && !oauthReturn) {
      return;
    }
    
    if (processedRef.current) {
      return;
    }
    
    // Additional check: prevent processing the same OAuth code twice
    if (code && processedCodesRef.current.has(code)) {
      return;
    }
    
    // Handle legacy token-based returns (your existing logic)
    if (token || (error && !oauthReturn)) {
      // ... your existing token handling code stays the same
      return;
    }

    // Handle new OAuth bridge returns
    if (oauthReturn === "true" && (code || error)) {
      processedRef.current = true;

      // Mark this OAuth code as processed
      if (code) {
        processedCodesRef.current.add(code);
      }

      try {
        if (error) {
          let errorMessage = "Възникна грешка при влизането с Google";

          switch (error) {
            case "access_denied":
              errorMessage = "Достъпът беше отказан";
              break;
            case "invalid_request":
              errorMessage = "Невалидна заявка";
              break;
            default:
              errorMessage = `Грешка: ${error}`;
          }

          getErrorToast(errorMessage);
          const cleanUrl = window.location.pathname;
          navigate(cleanUrl, { replace: true });
          return;
        }

        if (code && state) {
          setIsLoadingProfile(true);

          // Extra protection: Check if we're already processing this exact code
          const codeKey = `processing_${code}`;
          const isAlreadyProcessing = sessionStorage.getItem(codeKey);
          if (isAlreadyProcessing) {
            return;
          }

          // Mark this code as being processed
          sessionStorage.setItem(codeKey, 'true');

          try {
            // Call your backend to process the OAuth callback
            const response = await axiosInstance.get(
              endpointsMetadata.authentication.googleCallbackBridge,
              {
                params: { code, state },
              }
            );

            if (response.data.success && response.data.authToken) {
              // Fetch user profile using the token
              const userProfile = await fetchUserProfile(response.data.authToken);

              if (userProfile) {
                // A Google login started on /login?returnUrl=… comes back there: go on to that page.
                await onSuccessfulUserLogin(
                  userProfile,
                  getSafeReturnUrl(urlParams.get("returnUrl")),
                );
                getSuccessToast("Успешно влизане с Google!");
              }
            } else {
              getErrorToast(
                response.data.errorMessage || "Неуспешна автентикация"
              );
            }
          } catch (apiError: any) {
            getErrorToast("Грешка при автентикацията");
          } finally {
            // Clean up processing flag
            sessionStorage.removeItem(codeKey);
          }
        }

        // Clean up URL parameters
        const cleanUrl = window.location.pathname;
      } catch (err: any) {
        getErrorToast("Възникна грешка при автентикацията");
        const cleanUrl = window.location.pathname;
        navigate(cleanUrl, { replace: true });
      } finally {
        setIsLoadingProfile(false);
      }
    }
  }, [location.search, navigate, onSuccessfulUserLogin, fetchUserProfile]);

  useEffect(() => {
    handleGoogleAuthReturn();
  }, [handleGoogleAuthReturn]);

  // Legacy effect for direct backend redirects (simplified)
  useEffect(() => {
    const hasOAuthParams = new URLSearchParams(location.search).has('code') || 
                          new URLSearchParams(location.search).has('token') || 
                          new URLSearchParams(location.search).has('oauth_return');
    
    if (hasOAuthParams) {
      return;
    }
    
    // Only handle legacy googleAuthReturnUrl for non-wizard flows
    const storedReturnUrl = localStorage.getItem('googleAuthReturnUrl');
    if (storedReturnUrl && location.pathname !== storedReturnUrl) {
      localStorage.removeItem('googleAuthReturnUrl');
      setTimeout(() => {
        window.location.href = storedReturnUrl;
      }, 100);
    }
  }, [location.pathname, location.search]);

  return {
    handleGoogleAuthReturn,
    isLoadingProfile,
  };
};
